/* Tajweed colouring for duʿāʾ text (adhkar that aren't Qurʾān verses).

   Qurʾānic adhkar use Quran.com's published tajweed markup. Duʿāʾ have none,
   so this derives it from the vowel marks — and is deliberately conservative:
   a rule is coloured only when the marks make it certain, so a missing mark
   means a missing colour, never a wrong one. Output is the same markup the
   Qurʾān text uses, so TajweedText renders both identically.

   Rules: ghunnah (نّ مّ), ikhfāʾ / iqlāb / idghām of nūn sākinah and tanwīn,
   ikhfāʾ / idghām shafawī of mīm sākinah, qalqalah, the solar lām, hamzat
   al-waṣl, and madd (natural, connected, separated, necessary). */

const FATHA = 'َ', DAMMA = 'ُ', KASRA = 'ِ', SHADDA = 'ّ', SUKUN = 'ْ', SUKUN_Q = 'ۡ';
const MADDAH = 'ٓ', DAGGER_ALIF = 'ٰ', SMALL_MEEM = 'ۢ', SMALL_MEEM_LOW = 'ۭ';
const SILENT = /[\u06DF\u06E0\u06EC]/;
const TANWIN = new Set(['ً', 'ٌ', 'ٍ', 'ࣰ', 'ࣱ', 'ࣲ']);
const VOWELS = new Set([FATHA, DAMMA, KASRA, ...TANWIN]);
const isMark = (c: string) => /[ً-ٰٟۖ-ࣰۭ-ࣲ]/.test(c);
const isLetter = (c: string) => /[ء-غف-يٱ-ۓ]/.test(c);

const HAMZA = new Set(['ء', 'أ', 'إ', 'ؤ', 'ئ', 'آ']);
const QALQALAH = new Set(['ق', 'ط', 'ب', 'ج', 'د']);
const IKHFA = new Set(['ت', 'ث', 'ج', 'د', 'ذ', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ف', 'ق', 'ك']);
const SUN = new Set(['ت', 'ث', 'د', 'ذ', 'ر', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ل', 'ن']);

interface Cl { base: string; marks: string; word: number; letter: boolean; posInWord: number; rule?: string }

function clusters(text: string): Cl[] {
  const out: Cl[] = [];
  let word = 0, pos = 0;
  for (const ch of text) {
    if (isMark(ch) && out.length && out[out.length - 1].letter) { out[out.length - 1].marks += ch; continue; }
    if (isLetter(ch)) { out.push({ base: ch, marks: '', word, letter: true, posInWord: pos++ }); continue; }
    if (/\s|[،,.؛*﴿﴾()]/.test(ch)) { if (pos) { word++; pos = 0; } }
    out.push({ base: ch, marks: '', word, letter: false, posInWord: -1 });
  }
  return out;
}

const has = (c: Cl | undefined, m: string) => !!c && c.marks.includes(m);
const hasVowel = (c: Cl) => [...c.marks].some((m) => VOWELS.has(m));
const bare = (c: Cl) => !hasVowel(c) && !has(c, SUKUN) && !has(c, SUKUN_Q) && !has(c, SHADDA);
const tanwin = (c: Cl) => [...c.marks].some((m) => TANWIN.has(m));
const sakin = (c: Cl) => has(c, SUKUN) || has(c, SUKUN_Q);

export function duaTajweedMarkup(text: string): string {
  const cs = clusters(text);
  const letters = cs.map((c, i) => (c.letter ? i : -1)).filter((i) => i >= 0);
  const nextL = (i: number) => { const k = letters.indexOf(i); return k >= 0 && k + 1 < letters.length ? cs[letters[k + 1]] : undefined; };
  const prevL = (i: number) => { const k = letters.indexOf(i); return k > 0 ? cs[letters[k - 1]] : undefined; };
  // Is this word otherwise vowelled? A bare letter only counts as sākin in a vowelled word.
  const vowelledWord = (w: number) => cs.some((c) => c.word === w && c.letter && (hasVowel(c) || sakin(c) || has(c, SHADDA)));
  const lastInWord = (c: Cl) => !cs.some((x) => x.letter && x.word === c.word && x.posInWord > c.posInWord);
  // The silent alif / alif maqṣūrah written after tanwīn al-fatḥ.
  const skipTanwinAlif = (i: number) => {
    const n = nextL(i);
    if (n && n.word === cs[i].word && (n.base === 'ا' || n.base === 'ى') && bare(n)) return nextL(cs.indexOf(n));
    return n;
  };

  for (const i of letters) {
    const c = cs[i];
    if (c.rule) continue;
    const n = nextL(i);
    const nextWordStartsWasl = !!n && n.word !== c.word && n.base === 'ٱ';

    // Hamzat al-waṣl (not at the very start, where it is pronounced).
    if (c.base === 'ٱ' && i !== letters[0]) { c.rule = 'ham_wasl'; continue; }

    // Solar lām: ال / ٱل before a letter with shaddah.
    // (Not the lām of ٱللَّه: lām before lām isn't marked.)
    if (c.base === 'ل' && bare(c) && n && n.word === c.word && has(n, SHADDA) && SUN.has(n.base) && n.base !== 'ل') {
      const p = prevL(i);
      if (p && p.word === c.word && (p.base === 'ٱ' || p.base === 'ا') && p.posInWord <= 2) { c.rule = 'laam_shamsiyah'; continue; }
    }

    // Ghunnah on mushaddad nūn / mīm.
    if ((c.base === 'ن' || c.base === 'م') && has(c, SHADDA)) { c.rule = 'ghunnah'; continue; }

    // Qalqalah.
    if (QALQALAH.has(c.base) && sakin(c)) { c.rule = 'qalaqah'; continue; }

    // Nūn sākinah and tanwīn.
    const nunSakin = c.base === 'ن' && (sakin(c) || has(c, SMALL_MEEM) || (bare(c) && vowelledWord(c.word) && !!n));
    if (nunSakin || tanwin(c)) {
      const t = tanwin(c) ? skipTanwinAlif(i) : n;
      if (has(c, SMALL_MEEM) || has(c, SMALL_MEEM_LOW) || (t && t.base === 'ب')) { if (t) c.rule = 'iqlab'; continue; }
      if (t && t.word !== c.word) {
        if (t.base === 'ر' || t.base === 'ل') { c.rule = 'idgham_wo_ghunnah'; continue; }
        if ('ينمو'.includes(t.base)) { c.rule = 'idgham_ghunnah'; continue; }
      }
      if (t && IKHFA.has(t.base)) { c.rule = 'ikhfa'; continue; }
      if (!tanwin(c)) continue;
    }

    // Mīm sākinah.
    if (c.base === 'م' && (sakin(c) || (bare(c) && vowelledWord(c.word))) && n) {
      if (n.base === 'ب') { c.rule = 'ikhfa_shafawi'; continue; }
      if (n.base === 'م' && n.word !== c.word) { c.rule = 'idgham_shafawi'; continue; }
    }

    /* Madd — only the lengthened madds are coloured, as in the tajweed
       muṣḥaf this matches (natural 2-count madd is left plain):
         hamzah after it, same word (muttaṣil) or next word (munfaṣil) → 4–5
         shaddah / sukūn after it in the same word (lāzim)              → 6
       Madd before a pause (ʿāriḍ, 2/4/6) depends on where you stop, so it
       isn't coloured. */
    const p = prevL(i);
    // A letter marked silent (۟ ۠) or with tashīl (۬) isn't pronounced as a madd: أَنَا۠ أُحۡيِۦ, تَا۟يْـَٔسُوا۟.
    const maddLetter = p && p.word === c.word && !hasVowel(c) && !has(c, SHADDA) && !SILENT.test(c.marks) && (
      (c.base === 'ا' && has(p, FATHA)) ||
      (c.base === 'ى' && has(p, FATHA) && !sakin(c)) || // ىْ after fatḥah is a līn letter (شَىْءٍ), not madd
      (c.base === 'ي' && has(p, KASRA)) ||
      (c.base === 'و' && has(p, DAMMA))
    );
    if (maddLetter || has(c, DAGGER_ALIF) || has(c, MADDAH)) {
      const target = has(c, DAGGER_ALIF) && !maddLetter ? c : c; // the madd letter itself carries the colour
      if (n && HAMZA.has(n.base) && (n.word === c.word || lastInWord(c)) && !nextWordStartsWasl) { target.rule = 'madda_obligatory'; continue; }
      if (n && n.word === c.word && (has(n, SHADDA) || (sakin(n) && !lastInWord(n)))) { target.rule = 'madda_necessary'; continue; }
    }
  }

  return cs.map((c) => {
    const t = c.base + c.marks;
    return c.rule ? `<tajweed class=${c.rule}>${t}</tajweed>` : t;
  }).join('');
}
